import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Simple in-memory cache so multiple mounts of HotelDetailPage for the
// same hotel share a single network request during the session.
const CACHE = new Map();

/**
 * useRoomTestimonials(hotelSlug) — fetches admin-managed testimonials
 * for a given hotel from `/api/room-testimonials/{slug}`. Returns
 * `{ items, ready }` where `items` is an array of shaped testimonials
 * (or an empty array) and `ready` flips to true once the initial
 * fetch settles (success or error).
 *
 * The hook is intentionally forgiving: on any error we return an empty
 * list so callers fall back to their curated defaults instead of
 * showing an error state to guests.
 */
export default function useRoomTestimonials(hotelSlug) {
  const [state, setState] = useState(() => ({
    items: CACHE.get(hotelSlug) || [],
    ready: CACHE.has(hotelSlug),
  }));

  useEffect(() => {
    if (!hotelSlug) {
      setState({ items: [], ready: true });
      return undefined;
    }
    if (CACHE.has(hotelSlug)) {
      setState({ items: CACHE.get(hotelSlug), ready: true });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/room-testimonials/${hotelSlug}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        CACHE.set(hotelSlug, items);
        if (!cancelled) setState({ items, ready: true });
      } catch {
        CACHE.set(hotelSlug, []);
        if (!cancelled) setState({ items: [], ready: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hotelSlug]);

  return state;
}
